import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { resolveThumbnailUrl } from '../utils/hlsPlayer';
import { useAuth, usePlayer } from '../contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Play, UserPlus, UserCheck, ChevronLeft, Trash2, MoreHorizontal, Camera, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { FirstBeatBadgeMini } from '../components/FirstBeatBadge';
import { FoundingCreatorBadge } from '../components/FoundingCreatorBadge';
import { FounderBadge } from '../components/FounderBadge';

const API = process.env.REACT_APP_BACKEND_URL;

const BANNER_IMAGES = [
  "https://images.unsplash.com/photo-1762267684555-8250b6f7df2b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbmVvbiUyMGFic3RyYWN0JTIwY29uY2VydCUyMHN0YWdlJTIwYmFubmVyJTIwd2lkZXxlbnwwfHx8fDE3NzUxODYxODR8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1703795799612-27dbd1a9e17b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbmVvbiUyMGFic3RyYWN0JTIwY29uY2VydCUyMHN0YWdlJTIwYmFubmVyJTIwd2lkZXxlbnwwfHx8fDE3NzUxODYxODR8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1703795802595-22f4c10e53ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxkYXJrJTIwbmVvbiUyMGFic3RyYWN0JTIwY29uY2VydCUyMHN0YWdlJTIwYmFubmVyJTIwd2lkZXxlbnwwfHx8fDE3NzUxODYxODR8MA&ixlib=rb-4.1.0&q=85",
];

const MOCK_THUMBNAILS = [
  "https://images.unsplash.com/photo-1762279801044-3c37872f4534?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdsb3dpbmclMjBnZW9tZXRyaWMlMjBmbHVpZHxlbnwwfHx8fDE3NzUxMzczNjZ8MA&ixlib=rb-4.1.0&q=85",
  "https://images.pexels.com/photos/28583190/pexels-photo-28583190.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.unsplash.com/photo-1764258906659-e966a089ed7c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdsb3dpbmclMjBnZW9tZXRyaWMlMjBmbHVpZHxlbnwwfHx8fDE3NzUxMzczNjZ8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1722708773274-e791ab361dd4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxjeWJlcnB1bmslMjBmdXR1cmlzdGljJTIwZGolMjBjb25jZXJ0fGVufDB8fHx8MTc3NTEzNzM2Nnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1741344694428-f84a93bc4eca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxjeWJlcnB1bmslMjBmdXR1cmlzdGljJTIwZGolMjBjb25jZXJ0fGVufDB8fHx8MTc3NTEzNzM2Nnww&ixlib=rb-4.1.0&q=85",
];

const formatNum = (n) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n;
};

const VideoRow = ({ title, videos, onVideoClick, onDelete, isOwner, getThumbnail }) => {
  if (!videos || videos.length === 0) return null;
  return (
    <section className="mb-8">
      <h2 className="text-xl font-bold text-white px-6 mb-4">{title}</h2>
      <div className="px-6">
        {/* Mobile: horizontal scroll */}
        <div className="md:hidden flex gap-4 overflow-x-auto scrollbar-hide pb-2" style={{ scrollbarWidth: 'none' }}>
          {videos.map((video, i) => (
            <div key={video.id} className="w-40 flex-shrink-0 group relative">
              <div
                className="cursor-pointer"
                onClick={() => onVideoClick(video)}
                data-testid={`creator-video-${video.id}`}
              >
                <div className="relative aspect-square rounded-lg overflow-hidden bg-zinc-800 mb-2 shadow-md">
                  <img src={getThumbnail(video, i)} alt={video.title} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-end p-2">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-transform">
                      <Play className="w-5 h-5 text-white fill-white ml-0.5" />
                    </div>
                  </div>
                </div>
                <p className="text-sm font-semibold text-white truncate">{video.title}</p>
                <p className="text-xs text-zinc-500 truncate">{formatNum(video.views || 0)} views</p>
              </div>
              {isOwner && (
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(video); }}
                  className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-zinc-400 hover:text-red-400 hover:bg-black/80 opacity-0 group-hover:opacity-100 transition-all z-10"
                  data-testid={`delete-video-${video.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
        {/* Desktop: grid */}
        <div className="hidden md:grid grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-5">
          {videos.map((video, i) => (
            <div key={video.id} className="group relative">
              <div
                className="cursor-pointer"
                onClick={() => onVideoClick(video)}
                data-testid={`creator-video-${video.id}`}
              >
                <div className="relative aspect-square rounded-lg overflow-hidden bg-zinc-800 mb-2 shadow-md">
                  <img src={getThumbnail(video, i)} alt={video.title} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-end p-3">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-transform">
                      <Play className="w-6 h-6 text-black fill-black ml-0.5" />
                    </div>
                  </div>
                </div>
                <p className="text-base font-semibold text-white truncate">{video.title}</p>
                <p className="text-sm text-zinc-500 truncate">{formatNum(video.views || 0)} views</p>
              </div>
              {isOwner && (
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(video); }}
                  className="absolute top-2 right-2 p-2 rounded-full bg-black/60 text-zinc-400 hover:text-red-400 hover:bg-black/80 opacity-0 group-hover:opacity-100 transition-all z-10"
                  data-testid={`delete-video-${video.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const DEMO_ARTIST_VIDEOS = {};

const buildDemoProfile = (name) => {
  const decodedName = decodeURIComponent(name);
  const videos = DEMO_ARTIST_VIDEOS[decodedName] || [];
  const totalViews = videos.reduce((s, v) => s + (v.views || 0), 0);
  return {
    username: decodedName,
    videos,
    follower_count: Math.floor(totalViews / 10),
    stats: { total_videos: videos.length, total_views: totalViews },
    tier: null,
  };
};

export const CreatorProfilePage = () => {
  const { creatorId } = useParams();
  const navigate = useNavigate();
  const { axiosInstance, user, token, refreshUser } = useAuth();
  const { playVideo } = usePlayer();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const bannerInputRef = useRef(null);

  const isOwner = user?.id === creatorId;


  const fetchProfile = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/creator/${creatorId}/profile`);
      setProfile(res.data);
    } catch (err) {
      // Fallback: build demo profile for demo artists
      const demoProfile = buildDemoProfile(creatorId);
      if (demoProfile.videos.length > 0) {
        setProfile(demoProfile);
      } else {
        // Try decoded name
        const decoded = decodeURIComponent(creatorId);
        const fallback = buildDemoProfile(decoded);
        if (fallback.videos.length > 0) {
          setProfile(fallback);
        } else {
          // Generic fallback
          setProfile({
            username: decoded,
            videos: [],
            follower_count: 0,
            stats: { total_videos: 0, total_views: 0 },
            tier: null,
          });
        }
      }
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, creatorId]);


  const checkFollowing = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/creator/${creatorId}/is-following`);
      setFollowing(res.data.following);
    } catch (err) {
      // Endpoint may not exist or user not logged in - default to not following
      setFollowing(false);
    }
  }, [axiosInstance, creatorId]);

  useEffect(() => {
    fetchProfile();
    if (token) checkFollowing();
  }, [creatorId, fetchProfile, checkFollowing, token]);

  const handleFollow = async () => {
    if (!token) { toast.error('Sign in to follow creators'); return; }
    try {
      const res = await axiosInstance.post(`/creator/${creatorId}/follow`);
      setFollowing(res.data.following);
      toast.success(res.data.following ? 'Following!' : 'Unfollowed');
      fetchProfile();
    } catch (err) {
      toast.error('Failed to follow');
    }
  };

  const handleVideoClick = (video) => {
    navigate(`/video/${video.id}`, { state: { video } });
  };

  const handleDelete = async (video) => {
    setDeleteConfirm(video);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await axiosInstance.delete(`/videos/${deleteConfirm.id}`);
      toast.success('Video deleted');
      setDeleteConfirm(null);
      fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleBannerClick = () => {
    if (isOwner) {
      bannerInputRef.current?.click();
    }
  };

  const handleBannerChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Banner must be under 10MB');
      return;
    }

    setUploadingBanner(true);
    try {
      const formData = new FormData();
      formData.append('banner', file);

      await axiosInstance.post('/profile/banner', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Banner updated!');
      fetchProfile();
      if (refreshUser) await refreshUser();
    } catch (error) {
      console.error('Banner upload error:', error);
      toast.error('Failed to update banner');
    } finally {
      setUploadingBanner(false);
    }
  };

  const getThumbnail = (video) => {
    return resolveThumbnailUrl(video);
  };

  const getBannerUrl = () => {
    if (profile?.banner) return `${API}/api/files/${profile.banner}`;
    // Fallback to random banner
    const bannerIdx = creatorId ? creatorId.charCodeAt(0) % BANNER_IMAGES.length : 0;
    return BANNER_IMAGES[bannerIdx];
  };

  const avatarLetter = profile?.username?.[0]?.toUpperCase() || '?';

  if (loading) {
    return (
      <div className="pb-32">
        <div className="h-56 bg-zinc-900 animate-pulse" />
        <div className="flex flex-col items-center -mt-16 relative z-10">
          <div className="w-32 h-32 rounded-full bg-zinc-800 animate-pulse border-4 border-[#050505]" />
          <div className="h-6 bg-zinc-800 rounded animate-pulse w-40 mt-4" />
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const allVideos = profile.videos || [];
  const latestReleases = allVideos.slice(0, 10);
  const popular = [...allVideos].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 10);
  const collections = [...allVideos].sort((a, b) => (b.likes || 0) - (a.likes || 0)).slice(0, 10);

  return (
    <div className="pb-32">
      {/* Hidden file input for banner upload */}
      <input
        ref={bannerInputRef}
        type="file"
        accept="image/*"
        onChange={handleBannerChange}
        className="hidden"
      />

      {/* Banner */}
      <div 
        className={`relative h-56 sm:h-64 overflow-hidden ${isOwner ? 'cursor-pointer group' : ''}`}
        onClick={handleBannerClick}
      >
        <img
          src={getBannerUrl()}
          alt="Banner"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-[#050505]" />

        {/* Upload overlay for owner */}
        {isOwner && (
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            {uploadingBanner ? (
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            ) : (
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/60 text-white">
                <Camera className="w-5 h-5" />
                <span className="text-sm font-medium">Change Banner</span>
              </div>
            )}
          </div>
        )}

        {/* Back button */}
        <button
          onClick={(e) => { e.stopPropagation(); navigate(-1); }}
          className="absolute top-4 left-4 z-10 p-2 rounded-full bg-black/40 hover:bg-black/60 transition-colors"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Avatar + Name */}
      <div className="flex flex-col items-center -mt-16 relative z-10">
        <div className="w-32 h-32 rounded-full border-4 border-[#050505] bg-zinc-700 flex items-center justify-center shadow-xl overflow-hidden">
          {profile.avatar ? (
            <img 
              src={`${API}/api/files/${profile.avatar}`} 
              alt={profile.username}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-5xl font-bold text-white">{avatarLetter}</span>
          )}
        </div>

        <h1 className="text-2xl sm:text-3xl font-bold text-white mt-4" data-testid="creator-name">
          {profile.display_name || profile.username}
        </h1>

        {/* Badges row */}
        <div className="flex items-center gap-2 mt-2 flex-wrap justify-center">
          {profile.is_founder && <FounderBadge size="md" />}
          {/* Founding Creator # only shows while the creator currently has
              at least one active (non-deleted) upload. The number stays
              reserved on the user doc — if they upload again it returns. */}
          {!profile.is_founder && (profile.stats?.total_videos || 0) > 0 && (
            <FoundingCreatorBadge number={profile.founding_number} size="md" />
          )}
          {profile.tier && (
            <span
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold"
              style={{ background: `${profile.tier.color}20`, color: profile.tier.color }}
            >
              {profile.tier.badge} {profile.tier.name}
            </span>
          )}
          <FirstBeatBadgeMini />
        </div>

        <p className="text-sm text-zinc-500 mt-1">
          {formatNum(profile.follower_count || 0)} followers &bull; {profile.stats?.total_videos || 0} videos
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-center gap-3 mt-5 px-6">
        {!isOwner && (
          <Button
            onClick={handleFollow}
            variant={following ? 'outline' : 'default'}
            className={`rounded-full px-6 font-semibold ${
              following
                ? 'border-zinc-600 text-white hover:bg-zinc-800'
                : 'bg-white text-black hover:bg-zinc-200'
            }`}
            data-testid="follow-btn"
          >
            {following ? (
              <><UserCheck className="w-4 h-4 mr-2" />Following</>
            ) : (
              <><UserPlus className="w-4 h-4 mr-2" />Follow</>
            )}
          </Button>
        )}

        {isOwner && (
          <Button
            onClick={() => navigate('/studio')}
            className="rounded-full px-6 font-semibold bg-white hover:bg-zinc-200 text-black"
            data-testid="upload-new-btn"
          >
            Upload New Video
          </Button>
        )}
      </div>

      {/* About the Artist */}
      <div className="mt-8 mx-6 p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/50" data-testid="about-artist-section">
        <h2 className="text-lg font-bold text-white mb-3">About the Artist</h2>
        {profile.bio ? (
          <p className="text-sm text-zinc-300 leading-relaxed">{profile.bio}</p>
        ) : (
          <p className="text-sm text-zinc-500 italic">This artist hasn't added a bio yet.</p>
        )}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-xs text-zinc-500">
          {profile.created_at && (
            <span>Joined {new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
          )}
          {profile.stats?.total_views > 0 && (
            <span>{formatNum(profile.stats.total_views)} total views</span>
          )}
          {profile.stats?.total_likes > 0 && (
            <span>{formatNum(profile.stats.total_likes)} likes</span>
          )}
          {profile.country && (
            <span>{profile.country}</span>
          )}
        </div>
      </div>

      {/* Content Rows */}
      <div className="mt-10">
        <VideoRow
          title="Latest Releases"
          videos={latestReleases}
          onVideoClick={handleVideoClick}
          onDelete={handleDelete}
          isOwner={isOwner}
          getThumbnail={getThumbnail}
        />

        <VideoRow
          title="Popular"
          videos={popular}
          onVideoClick={handleVideoClick}
          onDelete={handleDelete}
          isOwner={isOwner}
          getThumbnail={getThumbnail}
        />

        {collections.length > 0 && allVideos.length >= 3 && (
          <VideoRow
            title="Collections"
            videos={collections}
            onVideoClick={handleVideoClick}
            onDelete={handleDelete}
            isOwner={isOwner}
            getThumbnail={getThumbnail}
          />
        )}

        {allVideos.length === 0 && (
          <div className="text-center py-16 px-6">
            <p className="text-zinc-500 text-lg">No videos yet</p>
            {isOwner && (
              <Button
                onClick={() => navigate('/studio')}
                className="mt-4 rounded-full bg-white hover:bg-zinc-200 text-black"
              >
                Upload Your First Video
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setDeleteConfirm(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 max-w-sm w-full mx-4"
              onClick={e => e.stopPropagation()}
              data-testid="delete-modal"
            >
              <h3 className="text-lg font-bold text-white mb-2">Delete Video</h3>
              <p className="text-sm text-zinc-400 mb-5">
                Are you sure you want to delete <span className="text-white font-medium">"{deleteConfirm.title}"</span>? This cannot be undone.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => setDeleteConfirm(null)}
                  variant="outline"
                  className="flex-1 rounded-full border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmDelete}
                  className="flex-1 rounded-full bg-red-600 hover:bg-red-500 text-white"
                  data-testid="confirm-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
